/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    Output,
    ViewChild,
} from '@angular/core';

import { Subscription } from 'rxjs';
import { ILogsSearchBarStates, LogsSearchBarStore } from '../../services/logs-search-bar.store';
import { LogsSearchBarDropdownService } from '../../services/logs-search-bar-dropdown.service';
import './logs-filter-input.component.less';

/**
 * @description
 *      Logs input component to manage input size, styling, and set search bar store state.
 * @author Yogesh Patil
 */
@Component({
    selector: 'logs-filter-input',
    templateUrl: './logs-filter-input.component.html',
})
export class LogsFilterInputComponent implements AfterViewInit, OnDestroy {
    /**
     * Flag to indicate whether input is an editable input,
     * which gives it some unique properties like resizing with input size.
     */
    @Input()
    public isEditable = false;

    /**
     * Placeholder for the input.
     */
    @Input()
    public placeholder = '';

    /**
     * Event emitters for input actions.
     */
    @Output()
    public onSubmit = new EventEmitter<void>();

    @Output()
    public onBackspace = new EventEmitter<void>();

    /**
     * Event emitter for keydown events. Used for keyboard controls.
     */
    @Output()
    public onKeydown = new EventEmitter<KeyboardEvent>();

    /**
     * Ref for search input element.
     */
    @ViewChild('searchInput')
    public searchInputRef: ElementRef<HTMLInputElement>;

    /**
     * Listen to keydown events on this component and propagate them to the onKeydown event
     * emitter.
     */
    @HostListener('keydown', ['$event'])
    public handleKeydown(event: KeyboardEvent): void {
        this.onKeydown.emit(event);
    }

    /**
     * Flag to track whether this input is currently focused on.
     */
    public isActive = false;

    /**
     * Value of the filter in the input.
     */
    public filter = '';

    /**
     * Subscribe to the filter value from the store.
     */
    private inputFilterValueSubscription: Subscription;

    /**
     * Subscribe to submit events. Submit the current filter in the input if it is active.
     */
    private readonly submitSubscription = this.logsSearchBarDropdownService.submit$.subscribe(
        () => {
            if (this.isActive) {
                this.submit();
            }
        },
    );

    /**
     * Subscribe to dropdown stage change events. Recofus the input if it is active.
     */
    private readonly dropdownStageChangeSubscription =
        this.logsSearchBarDropdownService.dropdownStageChange$.subscribe(() => {
            if (this.isActive) {
                this.refocus();
            }
        });

    constructor(
        public readonly logsSearchBarStore: LogsSearchBarStore<ILogsSearchBarStates>,
        public readonly logsSearchBarDropdownService: LogsSearchBarDropdownService,
    ) {}

    /**
     * @override
     * Initialize input size and focus into it if needed.
     */
    public ngAfterViewInit(): void {
        this.inputFilterValueSubscription = this.logsSearchBarStore.inputFilterValue$
            .subscribe(filter => {
                this.filter = filter;
                this.updateInputSize();
            });

        if (this.isEditable) {
            this.searchInputRef.nativeElement.focus();
        } else {
            this.logsSearchBarStore.primaryFilterInput = this.searchInputRef;
        }
    }

    /** @override */
    public ngOnDestroy(): void {
        this.inputFilterValueSubscription.unsubscribe();
        this.submitSubscription.unsubscribe();
        this.dropdownStageChangeSubscription.unsubscribe();
    }

    /**
     * Set this input to active and open the dropdown upon focus.
     */
    public handleFocus(): void {
        this.isActive = true;
        this.logsSearchBarDropdownService.openDropdown();
    }

    /**
     * Handle ngModel, input size, and update store.
     */
    public handleFilterChange(filter: string): void {
        // ngModel sets empty input to undefined; change to empty string for below
        if (!filter) {
            filter = '';
        }

        this.logsSearchBarStore.setInputFilterValue(filter);

        if (this.isEditable) {
            this.updateInputSize();
        }
    }

    /**
     * Submit the current filter value and remove the focus.
     */
    public submit(): void {
        this.isActive = false;
        this.searchInputRef.nativeElement.blur();
        this.onSubmit.emit();
    }

    /**
     * Refocus the input.
     */
    public refocus(): void {
        this.searchInputRef.nativeElement.focus();
    }

    /**
     * Change the size of the input to match the width of the current text
     * when the user is editing a filter.
     */
    private updateInputSize(): void {
        // Set a minimum size at 4
        const inputSize = Math.max(4, this.filter.length + 3);

        this.searchInputRef.nativeElement.setAttribute('size', inputSize.toString());
    }
}
